import { offerable, originLabel } from './leadFields';
import type { LeadSource } from './leadFields';

/**
 * Filtering the leads list by where somebody came from.
 *
 * ── The gap this closes ────────────────────────────────────────────────────
 *
 * Kyu, looking at the filter panel: "we should be able to see filters per
 * origin, right? Remember what Alex wants to see? they're not here." He was
 * right. Drop 23 shipped the Lead Origin vocabulary, the drawer field and the
 * grouped picker, plus chips for leads MISSING an origin — and never a way to
 * filter BY one. "Show me the Instagram leads" was impossible.
 *
 * ── Two levels, because eighteen chips is not a filter bar ─────────────────
 *
 * Kyu's shape: pick the group, and a second control appears for the value.
 * Against the real vocabulary that is nine chips on one row instead of
 * eighteen, and the second level never exceeds four:
 *
 *   Forms         Typeform, Website Form, Social Form
 *   Social media  Social Media, Instagram, Facebook, TikTok
 *   Advertising   LinkedIn Ads, Facebook Ads
 *   Direct        Email, Call
 *   Referral      Referral, Alex Contact
 *   Outbound      LinkedIn Outreach
 *   Event         Expo / In person
 *   Other         (no group)
 *   Unknown       (no group)
 *
 * THE GROUP ALONE ALREADY FILTERS. "How many came from social" is the common
 * question and it costs one click; the second control NARROWS rather than
 * being a required step. A group of one never shows it at all — a dropdown
 * with a single option is ceremony, and it teaches people the control is
 * pointless right before they meet one that is not.
 *
 * ── A value with no group is its own group ─────────────────────────────────
 *
 * Other, Unknown and the retired LinkedIn keep a NULL group deliberately
 * (20261216000100: "so they render at the end rather than being filed under a
 * heading that would make them look like a considered choice"). Rather than an
 * "Ungrouped" bucket, each becomes a group of one named after itself. One
 * rule, no special case, and the chip says the thing it filters to.
 */

export interface OriginGroup {
  /** What the chip says. For an ungrouped value, the value's own name. */
  name: string;
  /** In `position` order, as the picker shows them. */
  values: LeadSource[];
}

/**
 * The chips, in the order the picker already uses.
 *
 * Retired values are dropped by `offerable` — the same rule the drawer's
 * picker applies. A filter that offers a value nobody can choose any more is a
 * control that can only ever return the past.
 */
export function originGroups(sources: readonly LeadSource[]): OriginGroup[] {
  const groups: OriginGroup[] = [];
  const byName = new Map<string, OriginGroup>();
  for (const source of offerable([...sources])) {
    const label = (source.group_name ?? '').trim();
    const key = label === '' ? String(source.name) : label;
    const held = byName.get(key);
    if (held === undefined) {
      const made: OriginGroup = { name: key, values: [source] };
      byName.set(key, made);
      groups.push(made);
    } else {
      held.values.push(source);
    }
  }
  return groups;
}

/** Whether this group is worth a second control at all. */
export function hasChoiceWithin(group: OriginGroup): boolean {
  return group.values.length > 1;
}

/**
 * What one value inside a group is called in the second control.
 *
 * ⚠ THE ONE THAT IS EASY TO GET WRONG. There is a value literally named
 * "Social Media" sitting INSIDE the Social media group — the answer for a lead
 * where somebody knew it was social but not which platform. Listing it as
 * "Social Media" next to Instagram and Facebook reads as "all of them", so
 * picking it would silently return a quarter of what the reader expected.
 *
 * It is named for what it is instead: "Social media (not specified)".
 */
export function valueLabel(group: OriginGroup, source: LeadSource): string {
  const name = String(source.name);
  if (name.trim().toLowerCase() === group.name.trim().toLowerCase()) {
    return `${group.name} (not specified)`;
  }
  return originLabel(source);
}

/**
 * Every origin id a chosen group covers.
 *
 * The map matching reads from, built once by the component rather than looked
 * up per lead. Empty for a group nobody picked.
 */
export function idsInGroup(groups: readonly OriginGroup[], groupName: string | null): string[] {
  if (groupName === null) {
    return [];
  }
  const group = groups.find((entry) => entry.name === groupName);
  return group === undefined ? [] : group.values.map((value) => String(value.id));
}

/**
 * Origin id → the chip that would select it.
 *
 * Passed into `filterLeads` the way `leadsWithNextAction` and `evidenceByLead`
 * already are: derived context the filter needs and does not own. Empty until
 * the vocabulary loads, and the filter treats empty as "do not filter" rather
 * than "match nothing" — showing everything is visibly wrong, while showing
 * nothing looks like a real answer.
 */
export function originGroupById(groups: readonly OriginGroup[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const group of groups) {
    for (const value of group.values) {
      out.set(String(value.id), group.name);
    }
  }
  return out;
}
